import { Slot } from '@radix-ui/react-slot';
import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { buttonVariants } from './button-variants';
import { withTooltip } from './with-tooltip';

function extractText(children: React.ReactNode): string {
	return React.Children.toArray(children)
		.map((child) => {
			if (typeof child === 'string' || typeof child === 'number') {
				return child;
			}

			if (React.isValidElement(child)) {
				return extractText(
					(child.props as { children: React.ReactNode }).children,
				);
			}

			return '';
		})
		.join(' ');
}

export type HrefRenderArgs = {
	href: string;
	target?: React.HTMLAttributeAnchorTarget;
	label: string;
	noRef?: boolean;
	viewTransition?: ViewTransition;
};

export type ViewTransition =
	| { types: Array<'fade' | 'slide-left' | 'slide-right'> }
	| boolean;

export type ButtonProps = {
	asChild?: boolean;
	href?: string;
	as?: React.ElementType;
	loading?: boolean;
	icon?: string;
	noRef?: boolean;
	target?: React.HTMLAttributeAnchorTarget;
	viewTransition?: ViewTransition;
	/** Replaces the default `<a>` overlay, e.g. with a router-aware link. */
	renderHref?: (args: HrefRenderArgs) => React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
	VariantProps<typeof buttonVariants>;

const _Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			icon,
			size,
			href,
			noRef,
			loading,
			asChild = false,
			as,
			disabled,
			target,
			children,
			viewTransition,
			renderHref,
			...props
		},
		ref,
	) => {
		const Comp = as ?? (asChild ? Slot : 'button');
		return React.createElement(
			Comp,
			{
				disabled: loading || disabled,
				ref,
				className: cn(
					'relative isolate',
					buttonVariants({ variant, size, className }),
				),
				...props,
			},
			<>
				{href &&
					(() => {
						const label =
							extractText(children) ||
							(target === '_blank' ? m.btn_open_link_new() : m.btn_open_link());
						return renderHref ? (
							renderHref({ href, target, label, noRef, viewTransition })
						) : (
							// NOTE: stretched-link overlay. aria-label names it for AT;
							// biome flags empty anchors despite the label.
							// biome-ignore lint/a11y/useAnchorContent: overlay anchor is named via aria-label
							<a
								href={href}
								target={target}
								className="absolute inset-0"
								aria-label={label}
							/>
						);
					})()}
				{icon && <Iconify icon={icon} className="size-5" />}
				{children}
			</>,
		);
	},
);
_Button.displayName = '_Button';
const Button = withTooltip(_Button);

export { Button };
